import React, { useEffect } from 'react';
import { Button, Form, Row, Col, Dropdown, InputGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { fetchEvents } from '../../../../../../../redux/slices/eventsSlice';
import { LoadingSelect } from 'components/forms/LoadingFields';

type RuntimeLimitsProps = {
  state: any;
  onChange: (e: any) => void;
  isLoading?: boolean;
};

type RuntimeLimitFieldProps = {
  controlId: string;
  label: string;
  fieldName: string;
  value: string;
  helpText: string;
  parameterNames: string[];
  onTextChange: (fieldName: string, value: string) => void;
  onInsertParameter: (fieldName: string, parameterName: string) => void;
  disabled?: boolean;
};

const RuntimeLimitField: React.FC<RuntimeLimitFieldProps> = ({
  controlId,
  label,
  fieldName,
  value,
  helpText,
  parameterNames,
  onTextChange,
  onInsertParameter,
  disabled = false,
}) => {
  const hasValue = value.trim() !== '';

  return (
    <Form.Group className="mb-2" controlId={controlId}>
      <Form.Label>{label}</Form.Label>
      <InputGroup>
        <Form.Control
          type="text"
          aria-label={label}
          value={value}
          onChange={(e) => onTextChange(fieldName, e.target.value)}
          placeholder="HH:mm:ss, {{parameter_name}}, or =..."
          disabled={disabled}
          spellCheck={false}
        />
        <Dropdown align="end">
          <Dropdown.Toggle
            variant="outline-secondary"
            id={`${controlId}-parameters`}
            aria-label={`Insert parameter for ${label}`}
            disabled={disabled || parameterNames.length === 0}
          >
            Parameters
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {parameterNames.map((parameterName) => (
              <Dropdown.Item key={parameterName} onClick={() => onInsertParameter(fieldName, parameterName)}>
                {parameterName}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <Button variant="outline-secondary" aria-label={`Clear ${label}`} onClick={() => onTextChange(fieldName, '')} disabled={disabled || !hasValue}>
          Clear
        </Button>
      </InputGroup>
      <Form.Text className="text-muted">
        {helpText} Accepts <code>HH:MM:SS</code> or <code>{'{{parameter_name}}'}</code> or <code>{'={{parameter_name}} * 1000'}</code>.
      </Form.Text>
    </Form.Group>
  );
};

const RuntimeLimits: React.FC<RuntimeLimitsProps> = ({ state, onChange, isLoading = false }) => {
  const dispatch = useAppDispatch();
  const events = useSelector((s: any) => s.events?.items || []);
  const isEventsLoading = useSelector((s: any) => s.events?.loading?.fetchEvents || false);

  useEffect(() => {
    dispatch<any>(fetchEvents({}));
  }, [dispatch]);

  const processEvents = events.filter((event: any) => event.type === 'processEvent');

  const parameterNames = Array.from(new Set((state?.parameters || []).map((parameter: any) => parameter?.name).filter(Boolean))) as string[];

  const emitTextChange = (fieldName: string, value: string) => {
    onChange({
      target: { name: fieldName, value, type: 'text' },
    });
  };

  const handleInsertParameter = (fieldName: string, parameterName: string) => {
    emitTextChange(fieldName, `{{${parameterName}}}`);
  };

  return (
    <div className="mb-3">
      <Form.Label className="fw-bold">Runtime Limits</Form.Label>
      <Row>
        <Col md={6} xl={3}>
          <RuntimeLimitField
            controlId="form-RuntimeLimitMin"
            label="Minimum"
            fieldName="runtime_limit_min_hhmmss"
            value={state?.runtime_limit_min_hhmmss || ''}
            helpText="Fails if job completes faster."
            parameterNames={parameterNames}
            onTextChange={emitTextChange}
            onInsertParameter={handleInsertParameter}
            disabled={isLoading}
          />
        </Col>
        <Col md={6} xl={3}>
          <RuntimeLimitField
            controlId="form-RuntimeLimitMax"
            label="Maximum"
            fieldName="runtime_limit_max_hhmmss"
            value={state?.runtime_limit_max_hhmmss || ''}
            helpText="Fails if job runs longer."
            parameterNames={parameterNames}
            onTextChange={emitTextChange}
            onInsertParameter={handleInsertParameter}
            disabled={isLoading}
          />
        </Col>
        <Col md={6} xl={3}>
          <LoadingSelect
            label="Event on Exceed"
            controlId="form-RuntimeLimitEvent"
            name="runtime_limit_event"
            value={state?.runtime_limit_event || ''}
            onChange={onChange}
            isLoading={isEventsLoading}
            loadingLabel="Loading events..."
            placeholder="-- None --"
            options={processEvents.map((event: any) => ({
              value: event.event_id,
              label: event.name,
            }))}
            groupClassName="mb-2"
          />
        </Col>
        <Col md={6} xl={3}>
          <Form.Group className="mb-2" controlId="form-RuntimeLimitTerminate">
            <Form.Check
              type="checkbox"
              name="runtime_limit_terminate"
              label="Kill on exceed"
              checked={!!state?.runtime_limit_terminate}
              onChange={onChange}
              disabled={isLoading}
            />
            <Form.Text className="text-muted">Kill job when max runtime exceeded</Form.Text>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default RuntimeLimits;
